import { useEffect, useState, useRef } from "react";
import { http } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  User as UserIcon,
  Phone,
  Hash,
  Search,
  X as XIcon,
  UserPlus,
  Repeat2,
  BarChart3,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

/**
 * Customer picker — Phase B of CRM. Used from Retail (Πελάτης button)
 * and Delivery (Νέα παραγγελία). Searches by:
 *   • 6-digit code (or scanned barcode that encodes one)
 *   • Phone (4-10 digits)
 *   • Name (partial)
 *
 * Props:
 *   open: bool, onOpenChange: (bool) => void
 *   onSelect: (customer) => void  — receives the full customer doc
 *   onSelectWithRepeat: ({customer, lastOrder}) => void  — optional,
 *     called when user clicked the «Επανάληψη» quick-action on a row
 *     (fetches the customer's last order and bubbles it up).
 *   onSelectWithTopItems: (customer) => void  — optional, called when
 *     user clicked the «TOP» quick-action on a row. Caller is expected
 *     to attach the customer AND open the CustomerTopItemsDialog.
 *   showNewButton?: bool  — when true & no match, offers «Νέος Πελάτης»
 */
export default function CustomerPickerDialog({ open, onOpenChange, onSelect, onSelectWithRepeat, onSelectWithTopItems, showNewButton = true }) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState([]);
  const [searched, setSearched] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setQ("");
      setResults([]);
      setSearched(false);
      setTimeout(() => inputRef.current?.focus(), 60);
    }
  }, [open]);

  // Debounced live-search
  useEffect(() => {
    if (!open) return;
    if (!q.trim()) {
      setResults([]);
      setSearched(false);
      return;
    }
    const t = setTimeout(async () => {
      await runSearch(q.trim());
    }, 280);
    return () => clearTimeout(t);
  }, [q, open]);

  const runSearch = async (term) => {
    setBusy(true);
    setSearched(true);
    try {
      // Quick-path: pure 6-digit code → exact code lookup.
      const digitsOnly = term.replace(/\D/g, "");
      if (/^\d{6}$/.test(term)) {
        const r = await http.get(`/customers/lookup-by-code?code=${digitsOnly}`);
        setResults(r.data.found ? [r.data.customer] : []);
        return;
      }
      // Phone-exact lookup (10 digits).
      if (/^\d{10}$/.test(digitsOnly)) {
        const r = await http.get(`/customers/lookup-by-phone?phone=${digitsOnly}`);
        if (r.data.found) {
          setResults([r.data.customer]);
          return;
        }
      }
      // Fallback: full-text search across name / phone / code / vat.
      const r = await http.get(`/customers?q=${encodeURIComponent(term)}&limit=20`);
      setResults(r.data?.items || []);
    } catch (e) {
      toast.error("Σφάλμα αναζήτησης");
    } finally {
      setBusy(false);
    }
  };

  const pick = (c) => {
    onSelect?.(c);
    onOpenChange(false);
  };

  const pickAndRepeat = async (c, e) => {
    e?.stopPropagation();
    try {
      const r = await http.get(`/customers/${c.id}/last-order`);
      if (!r.data?.found) {
        toast.info("Δεν υπάρχει προηγούμενη παραγγελία");
        return;
      }
      onSelectWithRepeat?.({ customer: c, lastOrder: r.data });
      onOpenChange(false);
    } catch (err) {
      toast.error("Σφάλμα φόρτωσης τελευταίας παραγγελίας");
    }
  };

  const pickAndShowTop = (c, e) => {
    e?.stopPropagation();
    onSelectWithTopItems?.(c);
    onOpenChange(false);
  };

  const createNew = () => {
    onOpenChange(false);
    const phone = /^\d{10}$/.test(q.replace(/\D/g, "")) ? q.replace(/\D/g, "") : "";
    navigate(`/customers?new=1${phone ? `&phone=${phone}` : ""}`);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="customer-picker-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserIcon size={20} className="text-blue-600" /> Επιλογή Πελάτη
          </DialogTitle>
          <DialogDescription>
            Αναζητήστε με κωδικό (6 ψηφία), τηλέφωνο, όνομα ή σκανάρετε barcode.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              ref={inputRef}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="π.χ. 000023, 6912345678, Παπαδ..."
              className="pl-10 h-12 text-base"
              data-testid="customer-picker-search"
              autoFocus
            />
          </div>

          <div className="max-h-[50vh] overflow-y-auto space-y-1.5">
            {busy && (
              <div className="text-xs text-slate-400 italic px-1">Αναζήτηση...</div>
            )}
            {!busy && results.length === 0 && searched && (
              <Card className="p-4 bg-amber-50 border-amber-300 space-y-2" data-testid="customer-picker-empty">
                <div className="text-sm text-amber-900">
                  Δεν βρέθηκε πελάτης για «{q}».
                </div>
                {showNewButton && (
                  <Button
                    onClick={createNew}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid="customer-picker-new"
                  >
                    <UserPlus size={14} className="mr-1.5" /> Νέος Πελάτης
                  </Button>
                )}
              </Card>
            )}
            {!busy && results.length === 0 && !searched && (
              <div className="text-center text-slate-400 text-sm py-8 italic">
                Πληκτρολογήστε για αναζήτηση
              </div>
            )}
            {results.map((c) => {
              const hasOrders = (c.orders_count || 0) > 0;
              const canRepeat = !!onSelectWithRepeat && hasOrders;
              const canTop = !!onSelectWithTopItems && hasOrders;
              return (
                <div
                  key={c.id}
                  className="w-full bg-white hover:bg-emerald-50 border-2 border-slate-200 hover:border-emerald-500 rounded-md transition-all flex items-stretch overflow-hidden"
                >
                  <button
                    type="button"
                    onClick={() => pick(c)}
                    className="flex-1 text-left px-3 py-2.5 flex items-center gap-3 min-w-0"
                    data-testid={`customer-picker-row-${c.id}`}
                  >
                    <div className="flex flex-col items-center justify-center w-14 shrink-0">
                      <Hash size={11} className="text-blue-500" />
                      <span className="text-blue-700 font-mono text-xs font-bold">{c.code || "—"}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-slate-900 truncate">{c.name}</div>
                      <div className="text-xs text-slate-500 flex items-center gap-3 mt-0.5">
                        {(c.phones || []).length > 0 && (
                          <span className="flex items-center gap-1 font-mono">
                            <Phone size={11} /> {(c.phones || []).join(" · ")}
                          </span>
                        )}
                        {c.vat_no && <span className="font-mono">ΑΦΜ {c.vat_no}</span>}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-xs text-emerald-700 font-bold font-mono">
                        {Number(c.total_spent || 0).toFixed(2)}€
                      </div>
                      <div className="text-[10px] text-slate-400">
                        {(c.orders_count || 0)} παραγγ.
                      </div>
                    </div>
                  </button>
                  {canTop && (
                    <button
                      type="button"
                      onClick={(e) => pickAndShowTop(c, e)}
                      className="shrink-0 px-3 border-l-2 border-slate-200 bg-purple-50 hover:bg-purple-100 text-purple-700 font-bold flex flex-col items-center justify-center gap-0.5 transition-colors"
                      data-testid={`customer-picker-top-${c.id}`}
                      title="Επιλογή + Top Προϊόντα Πελάτη"
                    >
                      <BarChart3 size={16} />
                      <span className="text-[10px] uppercase tracking-wide">Top</span>
                    </button>
                  )}
                  {canRepeat && (
                    <button
                      type="button"
                      onClick={(e) => pickAndRepeat(c, e)}
                      className="shrink-0 px-3 border-l-2 border-slate-200 bg-blue-50 hover:bg-blue-100 text-blue-700 font-bold flex flex-col items-center justify-center gap-0.5 transition-colors"
                      data-testid={`customer-picker-repeat-${c.id}`}
                      title="Επιλογή + Επανάληψη Τελευταίας Παραγγελίας"
                    >
                      <Repeat2 size={16} />
                      <span className="text-[10px] uppercase tracking-wide">Επανάληψη</span>
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="customer-picker-cancel"
          >
            <XIcon size={14} className="mr-1" /> Κλείσιμο
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
